import React, { useMemo } from "react";
import { Card, Col, Placeholder, Badge } from "react-bootstrap";
import { Link } from "react-router-dom";
import { useFetchEvenementQuery } from "features/evenements/evenementSlice";

const typeColors: Record<string, string> = {
  examen: "danger",
  réunion: "warning",
  sortie: "info",
  fête: "success",
  sport: "primary",
};

const getTypeBadge = (type: string) => {
  const lower = (type || "").toLowerCase();
  const color = typeColors[lower] || "secondary";
  return <Badge bg={color} className="fs-11">{type || "Autre"}</Badge>;
};

const formatDate = (d: string) => {
  if (!d) return "-";
  try {
    return new Date(d).toLocaleDateString("fr-TN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return d;
  }
};

const EvenementsWidget = () => {
  const { data: evenements = [], isLoading, isError } = useFetchEvenementQuery();

  const upcoming = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return [...evenements]
      .filter((e) => {
        if (!e.creation_date) return false;
        return new Date(e.creation_date) >= now;
      })
      .sort(
        (a, b) =>
          new Date(a.creation_date).getTime() - new Date(b.creation_date).getTime()
      )
      .slice(0, 5);
  }, [evenements]);

  return (
    <Col xxl={4} lg={6}>
      <Card className="card-height-100">
        <Card.Header className="d-flex align-items-center">
          <h5 className="card-title mb-0 flex-grow-1">
            <i className="ri-calendar-event-line me-2 text-primary" />
            Événements à venir
          </h5>
          <Link to="/evenements" className="btn btn-soft-info btn-sm">
            Voir tout <i className="ri-arrow-right-s-line" />
          </Link>
        </Card.Header>
        <Card.Body>
          {isLoading ? (
            <Placeholder as="div" animation="glow">
              {[1, 2, 3].map((n) => (
                <Placeholder key={n} xs={12} className="mb-2" style={{ height: 32 }} />
              ))}
            </Placeholder>
          ) : isError ? (
            <div className="text-center text-danger py-3">
              <i className="ri-error-warning-line fs-2 d-block mb-2" />
              Impossible de charger les événements
            </div>
          ) : upcoming.length === 0 ? (
            <div className="text-center text-muted py-3">
              <i className="ri-calendar-line fs-2 d-block mb-2" />
              Aucun événement à venir
            </div>
          ) : (
            <div className="d-flex flex-column gap-3">
              {upcoming.map((evt, i) => (
                <div
                  key={i}
                  className="d-flex align-items-start gap-3 p-2 rounded bg-light"
                >
                  <div
                    className="text-center flex-shrink-0 bg-primary-subtle rounded p-2"
                    style={{ minWidth: 50 }}
                  >
                    <div className="fw-bold text-primary fs-16">
                      {new Date(evt.creation_date).getDate()}
                    </div>
                    <div className="text-muted fs-11 text-uppercase">
                      {new Date(evt.creation_date).toLocaleDateString("fr-TN", {
                        month: "short",
                      })}
                    </div>
                  </div>
                  <div className="flex-grow-1 overflow-hidden">
                    <h6 className="mb-1 text-truncate fs-14">{evt.titre}</h6>
                    <div className="d-flex align-items-center gap-2">
                      {getTypeBadge(evt.type)}
                      {evt.classes && evt.classes.length > 0 && (
                        <span className="text-muted fs-12">
                          {evt.classes.length} classe{evt.classes.length > 1 ? "s" : ""}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card.Body>
      </Card>
    </Col>
  );
};

export default EvenementsWidget;
